import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { MsalService } from '@azure/msal-angular';
import { TokenExtensions } from '../extensions/token-extensions';
import { ImpersonateService } from 'app/profile/impersonate-user/impersonate.service';
import { ToastrService } from 'ngx-toastr';
import { ToastTypesEnum } from '../enums/toast.enum';

/**
 * Append headers and token on each http request`.
 */
@Injectable({
  providedIn: 'root',
})
export class RequestHeadersInterceptor implements HttpInterceptor {
  private refreshTokenInProgress = false;
  private refreshTokenSubject: Subject<any> = new BehaviorSubject<any>(null);
  private idToken: any;

  constructor(
    private msalService: MsalService,
    private toastr: ToastrService,
    private impersonateService: ImpersonateService
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    this.addHeaders(request);

    if (!this.hasTokenExpired(this.idToken)) {
      return next.handle(this.addHeaders(request));
    }

    if (this.refreshTokenInProgress) {
      return this.refreshTokenSubject.pipe(
        filter((result) => result !== null),
        take(1),
        switchMap((res) => {
          return next.handle(this.addHeaders(request));
        })
      );
    }

    this.refreshTokenInProgress = true;
    this.refreshTokenSubject.next(null);
    const email = TokenExtensions.getParsedJwt(this.idToken.secret)
      ? TokenExtensions.getParsedJwt(this.idToken.secret).preferred_username
      : '';
    const accessTokenRequestSsoSilent = {
      loginHint: email,
      forceRefresh: true,
    };

    return this.msalService.ssoSilent(accessTokenRequestSsoSilent).pipe(
      catchError((err) => this.logout(err)),
      tap(console.log),
      switchMap((res) => {
        this.refreshTokenInProgress = false;
        this.refreshTokenSubject.next(request);
        return next.handle(this.addHeaders(request));
      })
    );

  }

  logout(err: any) {
    this.toastr.show('Your session has expired, the system failed to renew the session. Please log in again.', ToastTypesEnum.ERROR);
    setTimeout(() => {
      localStorage.clear();
      location.reload();
    }, 2000);
    return of(err);
  }

  hasTokenExpired(idToken: any) {
    if (!idToken) return true;
    const expDate = new Date(TokenExtensions.getParsedJwt(idToken.secret).exp * 1000);
    const dateNow = new Date();
    return expDate < dateNow;
  }

  addHeaders(request: HttpRequest<any>) {
    this.idToken = TokenExtensions.getTokenFromLocalStorage();

    let headers: HttpHeaders = new HttpHeaders();

    if (!request.url.includes('upload') && !request.url.includes('report-bug'))
      headers = headers.append('Content-Type', 'application/json; charset=utf-8');
    headers = headers.append('Accept', 'application/json; charset=utf-8');
    headers = headers.append('Authorization', 'Bearer ' + this.idToken.secret);
    headers = headers.append('Pragma', 'no-cache');
    headers = headers.append('Cache-Control', 'no-cache, no-store, must-revalidate, post-check=0, pre-check=0');
    headers = headers.append('Expires', '0');

    const impersonatedEmail = this.impersonateService.impersonatedUser?.email;

    if (impersonatedEmail) {
      headers = headers.append('Impersonated-Email', impersonatedEmail);
    }

    return request.clone({ headers });
  }
}
